import { useEffect, useMemo, useState } from 'react';
import * as adminService from '@/services/adminService';

const statusLabels = {
  pending: 'Pendente',
  validated: 'Validado',
  expired: 'Expirado',
  cancelled: 'Cancelado',
};

const statusStyles = {
  pending: 'border-accent/30 bg-accent/10 text-accent',
  validated: 'border-forest-light/30 bg-forest-light/10 text-forest-light',
  expired: 'border-fg/15 bg-fg/5 text-fg/45',
  cancelled: 'border-energy/30 bg-energy/10 text-energy',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('pt-PT');
};

export default function AdminRedemptionsPage() {
  const [redemptions, setRedemptions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const stats = useMemo(() => ({
    total: redemptions.length,
    pending: redemptions.filter((item) => item.status === 'pending').length,
    validated: redemptions.filter((item) => item.status === 'validated').length,
    expired: redemptions.filter((item) => item.status === 'expired').length,
  }), [redemptions]);

  const filtered = useMemo(() => {
    if (filter === 'all') return redemptions;
    return redemptions.filter((item) => item.status === filter);
  }, [filter, redemptions]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await adminService.getRedemptions({ limit: 100 });
      setRedemptions(res.data.data || []);
      setError('');
    } catch {
      setError('Não foi possível carregar os resgates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const updateStatus = async (redemption, status) => {
    setUpdatingId(redemption.id);
    setError('');
    setNotice('');
    try {
      await adminService.updateRedemptionStatus(redemption.id, status);
      setNotice(`Resgate marcado como ${statusLabels[status].toLowerCase()}.`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível atualizar o resgate.');
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">QR e resgates</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Monitor de Resgates</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">Acompanha QR codes emitidos, validações de parceiros e resgates pendentes.</p>
        </div>
        <button type="button" onClick={loadData} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
          Atualizar
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {[
          ['Total', stats.total],
          ['Pendentes', stats.pending],
          ['Validados', stats.validated],
          ['Expirados', stats.expired],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">{label}</div>
            <div className="mt-2 font-serif text-3xl font-black text-accent">{value}</div>
          </div>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Resgates recentes</h2>
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'validated', 'expired', 'cancelled'].map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => setFilter(status)}
                className={`rounded-full border px-3 py-1 text-xs font-bold ${filter === status ? 'border-accent/40 bg-accent/15 text-accent' : 'border-fg/10 bg-black/20 text-fg/50 hover:text-fg'}`}
              >
                {status === 'all' ? 'Todos' : statusLabels[status]}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Jogador</th>
                <th className="px-4 py-3">Oferta</th>
                <th className="px-4 py-3">Parceiro</th>
                <th className="px-4 py-3">QR</th>
                <th className="px-4 py-3">Emitido</th>
                <th className="px-4 py-3">Validade</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && filtered.length === 0 && <tr><td className="px-4 py-6 text-fg/45" colSpan="8">Nenhum resgate encontrado.</td></tr>}
              {filtered.map((redemption) => (
                <tr key={redemption.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-bold text-fg">{redemption.player?.displayName || redemption.player?.username || '-'}</div>
                    <div className="text-xs text-fg/35">{redemption.player?.username || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-fg/60">{redemption.offer?.title || '-'}</td>
                  <td className="px-4 py-3 text-fg/60">{redemption.offer?.partner?.name || '-'}</td>
                  <td className="px-4 py-3">
                    <div className="max-w-[170px] truncate font-mono text-xs text-fg/45">{redemption.qrCode || '-'}</div>
                  </td>
                  <td className="px-4 py-3 text-fg/60">{formatDateTime(redemption.redeemedAt)}</td>
                  <td className="px-4 py-3 text-fg/60">{formatDateTime(redemption.expiresAt)}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${statusStyles[redemption.status] || statusStyles.pending}`}>
                      {statusLabels[redemption.status] || redemption.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <select
                      className="rounded-lg border border-fg/10 bg-black/25 px-3 py-1.5 text-xs font-bold text-fg/70 outline-none focus:border-accent disabled:opacity-50"
                      value={redemption.status}
                      disabled={updatingId === redemption.id}
                      onChange={(event) => updateStatus(redemption, event.target.value)}
                    >
                      {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
